'use strict';

// ============================================================
// Babies & Blooms Gift Registry — registry.js
// Loaded on all registry pages and product pages.
// Vanilla JS (ES2020), no external dependencies.
// ============================================================

const BB = (() => {

	// --------------------------------------------------------
	// Config
	// --------------------------------------------------------
	function getApiBase() {
		return document.getElementById('registry-app')?.dataset?.api
			|| 'https://rodj.me/babies/api';
	}

	// --------------------------------------------------------
	// Session (localStorage)
	// --------------------------------------------------------
	const SESSION_SHARE  = 'bb_reg_share';
	const SESSION_MANAGE = 'bb_reg_manage';

	function getSession() {
		return {
			shareToken:  localStorage.getItem(SESSION_SHARE)  || '',
			manageToken: localStorage.getItem(SESSION_MANAGE) || ''
		};
	}
	function saveSession(shareToken, manageToken) {
		localStorage.setItem(SESSION_SHARE,  shareToken);
		localStorage.setItem(SESSION_MANAGE, manageToken);
	}
	function clearSession() {
		localStorage.removeItem(SESSION_SHARE);
		localStorage.removeItem(SESSION_MANAGE);
	}
	function hasSession() {
		const s = getSession();
		return !!(s.shareToken && s.manageToken);
	}

	// --------------------------------------------------------
	// URL helpers
	// --------------------------------------------------------
	function param(name) {
		return new URLSearchParams(window.location.search).get(name) || '';
	}

	// --------------------------------------------------------
	// DOM helpers
	// --------------------------------------------------------
	function el(id)           { return document.getElementById(id); }
	function esc(str) {
		if (str == null) return '';
		return String(str)
			.replace(/&/g, '&amp;')
			.replace(/</g, '&lt;')
			.replace(/>/g, '&gt;')
			.replace(/"/g, '&quot;');
	}
	function fmtDate(dateStr) {
		if (!dateStr) return '';
		try {
			// Date-only strings (YYYY-MM-DD) must be parsed as local time to avoid UTC off-by-one
			const parts = String(dateStr).split('T')[0].split('-');
			const d = new Date(+parts[0], +parts[1] - 1, +parts[2]);
			return d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
		} catch { return dateStr; }
	}
	function fmtMoney(val) {
		return '$' + parseFloat(val || 0).toFixed(2);
	}
	function msgHtml(type, msg) {
		return `<div class="registry-alert registry-alert--${esc(type)}">${esc(msg)}</div>`;
	}
	function spinner() {
		return '<div class="registry-loading"><span class="registry-spinner"></span> Loading&hellip;</div>';
	}

	// --------------------------------------------------------
	// API fetch wrapper
	// --------------------------------------------------------
	async function apiFetch(path, options = {}) {
		try {
			const url = getApiBase() + path;
			const headers = { 'Content-Type': 'application/json' };
			if (options.manageToken) headers['X-Registry-Manage-Token'] = options.manageToken;

			const res = await fetch(url, {
				method:  options.method || 'GET',
				headers,
				body:    options.body ? JSON.stringify(options.body) : undefined
			});
			const text = await res.text();
			let data;
			try { data = JSON.parse(text); } catch { data = { error: text }; }
			return { ok: res.ok, status: res.status, data };
		} catch (err) {
			return { ok: false, status: 0, data: { error: err.message } };
		}
	}

	// ============================================================
	// PAGE: CREATE REGISTRY
	// ============================================================
	function initCreatePage() {
		const app = el('registry-app');
		if (!app) return;
		app.innerHTML = `
			<div class="registry-form-wrap">
				<h2 class="registry-page-title">Create a Registry</h2>
				<p>Create a wish list for your baby shower, birthday, or special occasion.
				   Friends and family can shop directly from your registry.</p>
				<div id="registry-msg"></div>
				<form id="registry-create-form" class="registry-form" novalidate>
					<div class="registry-form-row">
						<label class="registry-label">First Name <span class="registry-req">*</span></label>
						<input class="registry-input" id="registry-firstName" type="text" name="firstName" maxlength="100" />
					</div>
					<div class="registry-form-row">
						<label class="registry-label">Last Name <span class="registry-req">*</span></label>
						<input class="registry-input" type="text" name="lastName" maxlength="100" />
					</div>
					<div class="registry-form-row">
						<label class="registry-label">Email Address <span class="registry-req">*</span></label>
						<input class="registry-input" type="email" name="email" maxlength="256" />
					</div>
					<div class="registry-form-row">
						<label class="registry-label">Event Type <span class="registry-req">*</span></label>
						<select class="registry-select" name="eventType">
							<option value="">Select&hellip;</option>
							<option value="Baby Shower">Baby Shower</option>
							<option value="Birthday">Birthday</option>
							<option value="Gender Reveal">Gender Reveal</option>
							<option value="Other">Other</option>
						</select>
					</div>
					<div class="registry-form-row">
						<label class="registry-label">Event Date <span class="registry-hint">(optional)</span></label>
						<input class="registry-input" type="date" name="eventDate" />
					</div>
					<div class="registry-form-row">
						<label class="registry-label">Baby&rsquo;s Name <span class="registry-hint">(optional)</span></label>
						<input class="registry-input" type="text" name="babyName" maxlength="100" />
					</div>
					<div class="registry-form-row">
						<label class="registry-label">Baby&rsquo;s Gender <span class="registry-hint">(optional)</span></label>
						<select class="registry-select" name="babyGender">
							<option value="">Prefer not to say</option>
							<option value="Boy">Boy</option>
							<option value="Girl">Girl</option>
							<option value="Surprise">Surprise</option>
						</select>
					</div>
					<div class="registry-form-row">
						<label class="registry-label">Greeting Message <span class="registry-hint">(optional &mdash; shown on your registry page)</span></label>
						<textarea class="registry-textarea" name="greetingMessage" maxlength="500" rows="3"></textarea>
					</div>
					<div class="registry-form-row">
						<label class="registry-label registry-checkbox-label">
							<input type="checkbox" id="reg-create-isSearchable" checked />
							Allow guests to find this registry by searching by name
						</label>
						<div class="registry-hint" style="margin-top:4px;">If unchecked, your registry can only be accessed via your direct share link.</div>
					</div>
					<div class="registry-form-row">
						<div id="registry-captcha"></div>
					</div>
					<div class="registry-form-row">
						<button type="submit" class="btn registry-btn-primary" id="registry-create-btn">
							Create My Registry
						</button>
					</div>
				</form>
			</div>`;

		// Auto-focus first name
		el('registry-firstName').focus();

		// Render Turnstile explicitly now that the container exists
		bbRenderTurnstile();

		el('registry-create-form').addEventListener('submit', onCreateSubmit);
	}

	// Turnstile widget ID — stored so we can getResponse() and reset() later
	let bbTurnstileWidgetId = null;

	function bbRenderTurnstile() {
		const container = el('registry-captcha');
		if (!container || bbTurnstileWidgetId !== null) return;
		if (window.turnstile) {
			bbTurnstileWidgetId = turnstile.render('#registry-captcha', {
				sitekey: '0x4AAAAAADq-ymrsbWD-rNqm'
			});
		} else {
			// Script still loading — poll until ready
			const interval = setInterval(() => {
				if (window.turnstile) {
					clearInterval(interval);
					bbRenderTurnstile();
				}
			}, 100);
		}
	}

	async function onCreateSubmit(e) {
		e.preventDefault();
		const form   = e.target;
		const msgEl  = el('registry-msg');
		const btn    = el('registry-create-btn');

		const firstName = form.firstName.value.trim();
		const lastName  = form.lastName.value.trim();
		const email     = form.email.value.trim();
		const eventType = form.eventType.value;

		const requiredFields = [
			{ field: form.firstName, value: firstName, name: 'First Name' },
			{ field: form.lastName,  value: lastName,  name: 'Last Name' },
			{ field: form.email,     value: email,     name: 'Email Address' },
			{ field: form.eventType, value: eventType, name: 'Event Type' },
		];
		const missing = requiredFields.filter(r => !r.value);
		if (missing.length) {
			const names = missing.map(r => r.name).join(', ');
			msgEl.innerHTML = msgHtml('error', `Please fill in: ${names}.`);
			missing[0].field.scrollIntoView({ behavior: 'smooth', block: 'center' });
			missing[0].field.focus();
			return;
		}

		let captchaToken = '';
		if (window.turnstile) {
			try { captchaToken = turnstile.getResponse(bbTurnstileWidgetId) || ''; } catch { /* ignore */ }
		}
		if (!captchaToken) {
			msgEl.innerHTML = msgHtml('error', 'Please complete the security check.');
			return;
		}

		btn.disabled = true;
		btn.textContent = 'Creating\u2026';
		msgEl.innerHTML = '';

		const body = {
			firstName,
			lastName,
			email,
			eventType,
			eventDate:       form.eventDate.value       || null,
			babyName:        form.babyName.value.trim() || null,
			babyGender:      form.babyGender.value      || null,
			greetingMessage: form.greetingMessage.value.trim() || null,
			isSearchable:    el('reg-create-isSearchable')?.checked !== false,
			captchaToken
		};

		const res = await apiFetch('/registry', { method: 'POST', body });

		if (res.ok && res.data.shareToken) {
			el('registry-app').innerHTML = `
				<div class="registry-form-wrap">
					<div class="registry-success">
						<h2>Registry Created!</h2>
						<p>We&rsquo;ve sent a verification email to <strong>${esc(email)}</strong>.</p>
						<p>Please check your inbox and click the verification link to activate your registry.
						   The link expires in 48 hours.</p>
						<p class="registry-hint">Don&rsquo;t see it? Check your spam folder.</p>
					</div>
				</div>`;
			el('registry-app').scrollIntoView({ behavior: 'smooth', block: 'start' });
		} else {
			const errMap = {
				captcha_failed:          'Captcha verification failed. Please try again.',
				missing_required_fields: 'Please fill in all required fields.',
				invalid_email:           'Please enter a valid email address.',
				invalid_characters:      'Your submission contains invalid characters (< or >). Please remove them.',
				invalid_event_type:      'Please select a valid event type.',
				rate_limit_exceeded:     'Too many requests. Please wait a while before trying again.',
				'field_too_long:greeting': 'Your greeting message is too long (max 500 characters).'
			};
			const msg = errMap[res.data?.error] || 'Something went wrong. Please try again.';
			msgEl.innerHTML = msgHtml('error', msg);
			btn.disabled    = false;
			btn.textContent = 'Create My Registry';
			if (window.turnstile) { try { turnstile.reset(bbTurnstileWidgetId); bbTurnstileWidgetId = null; bbRenderTurnstile(); } catch { /* ignore */ } }
		}
	}

	// ============================================================
	// PAGE: VERIFY EMAIL
	// ============================================================
	async function initVerifyPage() {
		const app = el('registry-app');
		if (!app) return;
		app.innerHTML = spinner();

		const verifyToken = param('v');
		if (!verifyToken) {
			app.innerHTML = msgHtml('error', 'Invalid verification link.');
			return;
		}

		const res = await apiFetch(`/registry/verify/${encodeURIComponent(verifyToken)}`);

		if (res.ok && res.data.success) {
			const shareToken  = res.data.shareToken;
			const manageToken = res.data.manageToken;
			const publicUrl   = `${location.origin}/pages/registry?r=${encodeURIComponent(shareToken)}`;
			const manageUrl   = `${location.origin}/pages/registry-manage?r=${encodeURIComponent(shareToken)}&m=${encodeURIComponent(manageToken)}`;

			// Save session so "Add to Registry" works immediately in this browser
			saveSession(shareToken, manageToken);

			app.innerHTML = `
				<div class="registry-form-wrap">
					<div class="registry-success">
						<h2 style="font-size:2em;font-weight:700;">Email Verified!</h2>
						<p>Your registry is now active. These links have also been sent to your email.</p>
						<p style="margin-top:20px;"><strong>Share this link with friends and family:</strong><br>
						   <a href="${publicUrl}" style="color:#1a6a1a;text-decoration:underline;">${publicUrl}</a></p>
						<p style="margin-top:16px;"><strong>Your private management link &mdash; bookmark this:</strong><br>
						   <a href="${manageUrl}" style="color:#1a6a1a;text-decoration:underline;">Manage My Registry</a></p>
					</div>
				</div>`;
			app.scrollIntoView({ behavior: 'smooth', block: 'start' });
		} else if (res.data?.error === 'expired_or_not_found') {
			app.innerHTML = `
				<div class="registry-form-wrap">
					<div class="registry-error-box">
						<h2>Link Expired or Not Found</h2>
						<p>This verification link has expired or is invalid.</p>
						<p><a href="/pages/registry-create">Create a new registry</a> &nbsp;|&nbsp;
						   <a href="/pages/registry-resend">Resend my management link</a></p>
					</div>
				</div>`;
			app.scrollIntoView({ behavior: 'smooth', block: 'start' });
		} else {
			app.innerHTML = msgHtml('error', 'Verification failed. Please try again or contact us.');
			app.scrollIntoView({ behavior: 'smooth', block: 'start' });
		}
	}

	// ============================================================
	// PAGE: SEARCH REGISTRIES
	// ============================================================
	function initSearchPage() {
		const app = el('registry-app');
		if (!app) return;
		app.innerHTML = `
			<div class="registry-search-wrap">
				<h2 class="registry-page-title">Find a Registry</h2>
				<form id="registry-search-form" class="registry-form registry-form--search" novalidate>
					<div class="registry-form-row registry-form-row--inline">
						<input class="registry-input" type="text" id="search-lastName"
							name="lastName" placeholder="Last name *" required autocomplete="family-name" />
						<input class="registry-input" type="text" id="search-firstName"
							name="firstName" placeholder="First name (optional)" autocomplete="given-name" />
						<button type="submit" class="btn registry-btn-primary">Search</button>
					</div>
				</form>
				<div id="registry-search-results"></div>
			</div>
			<div class="registry-search-create-cta">
				<p>Don&rsquo;t have a registry yet?</p>
				<a href="/pages/registry-create" class="btn registry-btn-primary">Create a Registry</a>
			</div>`;
		el('registry-search-form').addEventListener('submit', onSearchSubmit);

		// Auto-run if query params are present
		const lastName = param('lastName');
		if (lastName) {
			el('search-lastName').value = lastName;
			el('search-firstName').value = param('firstName');
			performSearch(lastName, param('firstName'));
		}
	}

	async function onSearchSubmit(e) {
		e.preventDefault();
		const lastName  = el('search-lastName')?.value.trim();
		const firstName = el('search-firstName')?.value.trim();
		if (!lastName) {
			el('registry-search-results').innerHTML = msgHtml('error', 'Please enter a last name.');
			return;
		}
		await performSearch(lastName, firstName);
	}

	async function performSearch(lastName, firstName) {
		const resultsEl = el('registry-search-results');
		resultsEl.innerHTML = spinner();

		let url = `/registry/search?lastName=${encodeURIComponent(lastName)}`;
		if (firstName) url += `&firstName=${encodeURIComponent(firstName)}`;

		const res = await apiFetch(url);
		if (!res.ok) {
			resultsEl.innerHTML = msgHtml('error', 'Search failed. Please try again.');
			return;
		}

		const results = Array.isArray(res.data) ? res.data : [];
		if (results.length === 0) {
			resultsEl.innerHTML = '<p class="registry-no-results">No registries found. Check the spelling and try again.</p>';
			return;
		}

		const cards = results.map(r => `
			<div class="registry-search-card">
				<div class="registry-search-info">
					<strong class="registry-search-name">${esc(r.firstName)} ${esc(r.lastName)}</strong>
					<span class="registry-search-event">${esc(r.eventType)}${r.eventDate ? ' &mdash; ' + fmtDate(r.eventDate) : ''}</span>
					${r.babyName ? `<span class="registry-search-baby">Baby: ${esc(r.babyName)}</span>` : ''}
					<span class="registry-search-count">${r.itemCount || 0} item${r.itemCount !== 1 ? 's' : ''}</span>
				</div>
				<a href="/pages/registry?r=${encodeURIComponent(r.shareToken)}"
				   class="btn registry-btn-secondary">View Registry</a>
			</div>`).join('');

		resultsEl.innerHTML = `<div class="registry-search-results-list">${cards}</div>`;
	}

	// ============================================================
	// PAGE: VIEW REGISTRY (public / guest)
	// ============================================================
	async function initViewPage() {
		const app = el('registry-app');
		if (!app) return;
		app.innerHTML = spinner();

		const shareToken = param('r');
		if (!shareToken) {
			app.innerHTML = `<div class="registry-error-box"><p>No registry specified.
				<a href="/pages/registry-search">Search for a registry</a>.</p></div>`;
			return;
		}

		const res = await apiFetch(`/registry/${encodeURIComponent(shareToken)}`);
		if (!res.ok) {
			app.innerHTML = `<div class="registry-error-box"><p>Registry not found or not active.
				<a href="/pages/registry-search">Search for another registry</a>.</p></div>`;
			return;
		}

		app.innerHTML = renderViewHtml(res.data);

		// Attach buy buttons
		app.querySelectorAll('.registry-buy-btn').forEach(btn => {
			btn.addEventListener('click', () => {
				const variantId = parseInt(btn.dataset.variantId);
				const itemId    = parseInt(btn.dataset.itemId);
				buyItem(variantId, 1, shareToken, itemId, btn);
			});
		});
	}

	function eventIcon(eventType) {
		if (!eventType) return '🎁';
		const t = eventType.toLowerCase();
		if (t.includes('shower')) return '🍼';
		if (t.includes('birthday')) return '🎂';
		if (t.includes('reveal')) return '✨';
		return '🎁';
	}

	function renderViewHtml(reg) {
		const items     = reg.items || [];
		const eventDate = reg.eventDate ? fmtDate(reg.eventDate) : null;
		const babyLine  = reg.babyName
			? `Baby ${esc(reg.babyName)}${reg.babyGender && reg.babyGender !== 'Surprise' ? ` &mdash; it&rsquo;s a ${esc(reg.babyGender)}!` : ''}`
			: null;
		return `
			<div class="registry-view">
				<div class="registry-hero">
					<div class="registry-hero-eyebrow">${eventIcon(reg.eventType)}&ensp;${esc(reg.eventType || 'Gift Registry')}</div>
					<h2 class="registry-hero-name">${esc(reg.firstName)} ${esc(reg.lastName)}&rsquo;s Registry</h2>
					${babyLine  ? `<p class="registry-hero-baby">${babyLine}</p>` : ''}
					${eventDate ? `<p class="registry-hero-event">${esc(eventDate)}</p>` : ''}
					${reg.greetingMessage ? `<div class="registry-hero-greeting">&ldquo;${esc(reg.greetingMessage)}&rdquo;</div>` : ''}
					${items.length ? `<p class="registry-hero-count">${items.length} item${items.length !== 1 ? 's' : ''} on this registry</p>` : ''}
				</div>
				${items.length === 0
					? '<p class="registry-empty">No items added yet.</p>'
					: `<div class="registry-items-grid">${items.map(renderViewItemCard).join('')}</div>`}
			</div>`;
	}

	function renderViewItemCard(item) {
		const stillNeeded     = Math.max(0, (item.qtyDesired || 1) - (item.qtyPurchased || 0));
		const fullyPurchased  = stillNeeded === 0;
		const productLink     = item.productUrl || '#';
		return `
			<div class="registry-item-card${fullyPurchased ? ' registry-item-card--purchased' : ''}">
${item.productImageUrl ? `
				<div class="registry-item-img">
					<a href="${esc(productLink)}"><img src="${esc(item.productImageUrl)}" alt="${esc(item.productTitle)}" loading="lazy" /></a>
				</div>` : ''}
				<div class="registry-item-body">
					<a href="${esc(productLink)}" class="registry-item-title">${esc(item.productTitle)}</a>
					${item.variantTitle ? `<p class="registry-item-variant">${esc(item.variantTitle)}</p>` : ''}
					${item.note         ? `<p class="registry-item-note">&ldquo;${esc(item.note)}&rdquo;</p>` : ''}
					<p class="registry-item-price">${fmtMoney(item.price)}</p>
					<p class="registry-item-qty-badge">
						${fullyPurchased
							? '<span class="registry-badge registry-badge--purchased">Fully Purchased &#10003;</span>'
							: `<span class="registry-badge registry-badge--needed">Still needed: ${stillNeeded}</span>`}
					</p>
				</div>
				<div class="registry-item-footer">
					${fullyPurchased
						? '<span class="registry-thank-you">Thank you!</span>'
						: `<button class="btn registry-btn-primary registry-buy-btn"
								data-variant-id="${item.shopifyVariantId}"
								data-item-id="${item.registryItemId}">Buy This Item</button>`}
				</div>
			</div>`;
	}

	async function buyItem(variantId, qty, shareToken, registryItemId, btn) {
		if (btn) { btn.disabled = true; btn.textContent = 'Adding to cart\u2026'; }
		try {
			const res = await fetch('/cart/add.js', {
				method:  'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({
					items: [{
						id:         variantId,
						quantity:   qty,
						properties: {
							'_registry_share_token': shareToken,
							'_registry_item_id':     String(registryItemId)
						}
					}]
				})
			});
			if (res.ok) {
				window.location.href = '/cart';
			} else {
				const data = await res.json().catch(() => ({}));
				const msg  = data?.description || data?.message || 'Could not add item to cart.';
				if (btn) { btn.disabled = false; btn.textContent = 'Buy This Item'; }
				window.alert(msg);
			}
		} catch {
			if (btn) { btn.disabled = false; btn.textContent = 'Buy This Item'; }
			window.alert('Could not add to cart. Please try again.');
		}
	}

	// ============================================================
	// PAGE: MANAGE REGISTRY (owner)
	// ============================================================
	async function initManagePage() {
		const app = el('registry-app');
		if (!app) return;
		app.innerHTML = spinner();

		// Tokens from URL take priority — save to session
		let shareToken  = param('r');
		let manageToken = param('m');
		if (shareToken && manageToken) {
			saveSession(shareToken, manageToken);
			// Clean up URL so the token isn't bookmarked/shared accidentally
			const cleanUrl = window.location.pathname + '?r=' + encodeURIComponent(shareToken);
			history.replaceState(null, '', cleanUrl);
		} else {
			const session = getSession();
			shareToken  = session.shareToken;
			manageToken = session.manageToken;
		}

		if (!shareToken || !manageToken) {
			app.innerHTML = `
				<div class="registry-error-box">
					<h2>Management Link Required</h2>
					<p>Please use your private management link to access this page.</p>
					<p><a href="/pages/registry-resend">Resend my management link</a></p>
				</div>`;
			return;
		}

		await loadManageView(shareToken, manageToken);
	}

	async function loadManageView(shareToken, manageToken) {
		const app = el('registry-app');
		if (!app) return;
		app.innerHTML = spinner();

		const res = await apiFetch(`/registry/${encodeURIComponent(shareToken)}/manage`, { manageToken });
		if (!res.ok) {
			app.innerHTML = `
				<div class="registry-error-box">
					<h2>Access Denied</h2>
					<p>Your management link is invalid or expired. Please request a new one.</p>
					<p><a href="/pages/registry-resend">Resend my management link</a></p>
				</div>`;
			clearSession();
			return;
		}

		const reg = res.data;
		app.innerHTML = renderManageHtml(reg, shareToken);
		attachManageEvents(shareToken, manageToken);
	}

	function renderManageHtml(reg, shareToken) {
		const publicUrl = `${window.location.origin}/pages/registry?r=${encodeURIComponent(shareToken)}`;
		const items     = reg.items || [];
		return `
			<div class="registry-manage">
				${items.length === 0 ? `
				<div class="registry-empty-banner">
					Your registry is empty. Tap &ldquo;Add to Registry&rdquo; on any product to get started.
					<a href="/collections/all" class="registry-empty-banner-cta">Start Shopping &rarr;</a>
				</div>` : ''}
				<div class="registry-manage-header">
					<h2 class="registry-page-title">Manage Your Registry</h2>
					<span class="registry-status-badge ${reg.isVerified ? 'registry-status-badge--active' : 'registry-status-badge--pending'}">
						${reg.isVerified ? '&#10003; Active' : '&#8987; Pending Verification'}
					</span>
				</div>

				<div class="registry-manage-section">
					<h3>Your Public Registry Link</h3>
					<div class="registry-share-link-row">
						<input class="registry-input registry-share-link-input" type="text"
							id="registry-share-url" value="${esc(publicUrl)}" readonly />
						<button class="btn registry-btn-secondary registry-copy-btn"
							data-copy="${esc(publicUrl)}">Copy</button>
						<a href="${esc(publicUrl)}" target="_blank"
							class="btn registry-btn-secondary">View</a>
					</div>
				</div>

				<div class="registry-manage-section">
					<h3>Registry Details</h3>
					<div id="registry-details-msg"></div>
					<form id="registry-details-form" class="registry-form">
						<div class="registry-form-row" style="display:flex;gap:12px;">
							<div style="flex:1;"><label class="registry-label">First Name <span class="registry-req">*</span></label>
								<input class="registry-input" id="reg-firstName" type="text" maxlength="100" value="${esc(reg.firstName)}" /></div>
							<div style="flex:1;"><label class="registry-label">Last Name <span class="registry-req">*</span></label>
								<input class="registry-input" id="reg-lastName" type="text" maxlength="100" value="${esc(reg.lastName)}" /></div>
						</div>
						<div class="registry-form-row" style="display:flex;gap:12px;">
							<div style="flex:1;"><label class="registry-label">Event Type <span class="registry-req">*</span></label>
								<select class="registry-select" id="reg-eventType">${["Baby Shower","Sip & See","Birthday","Other"].map(t => `<option value="${t}" ${reg.eventType===t?"selected":""}>${t}</option>`).join('')}</select></div>
							<div style="flex:1;"><label class="registry-label">Event Date</label>
								<input class="registry-input" id="reg-eventDate" type="date" value="${reg.eventDate ? reg.eventDate.split('T')[0] : ''}" /></div>
						</div>
						<div class="registry-form-row" style="display:flex;gap:12px;">
							<div style="flex:1;"><label class="registry-label">Baby&rsquo;s Name</label>
								<input class="registry-input" id="reg-babyName" type="text" maxlength="100" value="${esc(reg.babyName||'')}"/></div>
							<div style="flex:1;"><label class="registry-label">Gender</label>
								<select class="registry-select" id="reg-babyGender"><option value="">Prefer not to say</option>${["Boy","Girl","Surprise"].map(g => `<option value="${g}" ${reg.babyGender===g?"selected":""}>${g}</option>`).join('')}</select></div>
						</div>
						<div class="registry-form-row"><label class="registry-label">Greeting Message <span class="registry-hint">(shown to guests)</span></label>
							<textarea class="registry-textarea" id="reg-greetingMessage" rows="3" maxlength="500">${esc(reg.greetingMessage||'')}</textarea></div>
						<div class="registry-form-row">
							<label class="registry-label registry-checkbox-label">
								<input type="checkbox" id="reg-isSearchable" ${reg.isSearchable !== false ? 'checked' : ''} />
								Allow guests to find this registry by searching by name
							</label>
							<div class="registry-hint" style="margin-top:4px;">If unchecked, your registry can only be accessed via your direct share link.</div>
						</div>
						<button type="submit" class="btn registry-btn-primary">Save Details</button>
						<span id="registry-details-save-status" style="margin-left:12px;font-size:0.9em;"></span>
					</form>
				</div>

				<div class="registry-manage-section">
					<h3>Items (${items.length})</h3>
					${items.length === 0
						? `<div class="registry-empty-state">
								<p class="registry-empty-state-title">Your registry is empty</p>
								<p>Browse our store and click the <strong>&ldquo;Add to Registry&rdquo;</strong> button
								   on any product page to add items. Your session stays active as you browse.</p>
								<a href="/collections/all" class="btn registry-btn-primary registry-empty-state-cta">
									Start Shopping &rarr;
								</a>
							</div>`
						: `<div>
								<p class="registry-hint">Browse the store and use the &ldquo;Add to Registry&rdquo; button
								   on product pages to add items.</p>
								<div class="registry-manage-items">
									${items.map(i => renderManageItemRow(i)).join('')}
								</div>
							</div>`}
				</div>

				<div class="registry-manage-section registry-manage-danger-zone">
					<h3>Deactivate Registry</h3>
					<p>This will hide your registry from guests. Contact us to reactivate it.</p>
					<button class="btn registry-btn-danger" id="registry-deactivate-btn">Deactivate My Registry</button>
				</div>
			</div>`;
	}

	function renderManageItemRow(item) {
		const stillNeeded = Math.max(0, (item.qtyDesired || 1) - (item.qtyPurchased || 0));
		const iid         = item.registryItemId;
		return `
			<div class="registry-manage-item" id="manage-item-${iid}">
${item.productImageUrl ? `
				<div class="registry-manage-item-img">
					<img src="${esc(item.productImageUrl)}" alt="${esc(item.productTitle)}" />
				</div>` : ''}
				<div class="registry-manage-item-info">
					<strong>${esc(item.productTitle)}</strong>
					${item.variantTitle ? `<div class="registry-item-variant">${esc(item.variantTitle)}</div>` : ''}
					<div class="registry-item-price">${fmtMoney(item.price)}</div>
					<div class="registry-badge ${stillNeeded > 0 ? 'registry-badge--needed' : 'registry-badge--purchased'}">
						${stillNeeded > 0 ? `Still needed: ${stillNeeded}` : 'Fully purchased &#10003;'}
					</div>
				</div>
				<div class="registry-manage-item-controls">
					<label class="registry-qty-label">Qty:
						<input class="registry-input registry-qty-input" type="number"
							min="1" max="99" value="${item.qtyDesired || 1}"
							data-item-id="${iid}" />
					</label>
					<button class="registry-btn-small registry-btn-small--danger registry-remove-btn"
						data-item-id="${iid}">Remove</button>
				</div>
			</div>`;
	}

	function attachManageEvents(shareToken, manageToken) {
		// Copy link
		document.querySelectorAll('.registry-copy-btn').forEach(btn => {
			btn.addEventListener('click', () => {
				navigator.clipboard?.writeText(btn.dataset.copy).then(() => {
					const orig = btn.textContent;
					btn.textContent = 'Copied!';
					setTimeout(() => btn.textContent = orig, 2000);
				});
			});
		});

		// Auto-save qty on change
		document.querySelectorAll('.registry-qty-input').forEach(input => {
			let saveTimer = null;
			input.addEventListener('change', () => {
				clearTimeout(saveTimer);
				saveTimer = setTimeout(async () => {
					const iid = input.dataset.itemId;
					const qty = parseInt(input.value) || 1;
					await apiFetch(
						`/registry/${encodeURIComponent(shareToken)}/items/${iid}`,
						{ method: 'PUT', manageToken, body: { qtyDesired: qty, displayOrder: 0 } }
					);
				}, 400);
			});
		});

		// Remove item
		document.querySelectorAll('.registry-remove-btn').forEach(btn => {
			btn.addEventListener('click', async () => {
				if (!window.confirm('Remove this item from your registry?')) return;
				const iid = btn.dataset.itemId;
				btn.disabled = true; btn.textContent = 'Removing\u2026';
				const res = await apiFetch(
					`/registry/${encodeURIComponent(shareToken)}/items/${iid}`,
					{ method: 'DELETE', manageToken }
				);
				if (res.ok) {
					el(`manage-item-${iid}`)?.remove();
				} else {
					btn.disabled = false; btn.textContent = 'Remove';
					window.alert('Failed to remove item. Please try again.');
				}
			});
		});

		// Save registry details
		el('registry-details-form')?.addEventListener('submit', async (e) => {
			e.preventDefault();
			const statusEl = el('registry-details-save-status');
			const btn = e.target.querySelector('[type="submit"]');
			const firstName = el('reg-firstName')?.value.trim();
			const lastName  = el('reg-lastName')?.value.trim();
			const eventType = el('reg-eventType')?.value;
			if (!firstName || !lastName || !eventType) {
				statusEl.textContent = 'First name, last name, and event type are required.';
				statusEl.style.color = '#c62828';
				return;
			}
			btn.disabled = true; btn.textContent = 'Saving…';
			const res = await apiFetch(
				`/registry/${encodeURIComponent(shareToken)}`,
				{
					method: 'PUT', manageToken,
					body: {
						firstName,
						lastName,
						eventType,
						eventDate:       el('reg-eventDate')?.value || null,
						babyName:        el('reg-babyName')?.value.trim() || null,
						babyGender:      el('reg-babyGender')?.value || null,
						greetingMessage: el('reg-greetingMessage')?.value.trim() || null,
						isSearchable:    el('reg-isSearchable')?.checked !== false
					}
				}
			);
			btn.disabled = false; btn.textContent = 'Save Details';
			if (res.ok) {
				statusEl.textContent = 'Saved ✓';
				statusEl.style.color = '#2e7d32';
				setTimeout(() => statusEl.textContent = '', 3000);
			} else {
				statusEl.textContent = 'Save failed. Please try again.';
				statusEl.style.color = '#c62828';
			}
		});

		// Deactivate
		el('registry-deactivate-btn')?.addEventListener('click', async () => {
			if (!window.confirm('Are you sure you want to deactivate your registry? Guests will no longer be able to view it.')) return;
			const btn = el('registry-deactivate-btn');
			btn.disabled = true; btn.textContent = 'Deactivating\u2026';
			const res = await apiFetch(`/registry/${encodeURIComponent(shareToken)}`, { method: 'DELETE', manageToken });
			if (res.ok) {
				clearSession();
				el('registry-app').innerHTML = `
					<div class="registry-success">
						<h2>Registry Deactivated</h2>
						<p>Your registry has been deactivated. Contact us if you need to reactivate it.</p>
					</div>`;
				el('registry-app').scrollIntoView({ behavior: 'smooth', block: 'start' });
			} else {
				btn.disabled = false; btn.textContent = 'Deactivate My Registry';
				window.alert('Failed to deactivate. Please try again.');
			}
		});
	}

	// ============================================================
	// PAGE: RESEND MANAGEMENT LINK
	// ============================================================
	function initResendPage() {
		const app = el('registry-app');
		if (!app) return;
		app.innerHTML = `
			<div class="registry-form-wrap">
				<h2 class="registry-page-title">Resend My Management Link</h2>
				<p>Enter your email address and we&rsquo;ll send you your registry management link.</p>
				<div id="registry-msg"></div>
				<form id="registry-resend-form" class="registry-form" novalidate>
					<div class="registry-form-row">
						<label class="registry-label">Email Address <span class="registry-req">*</span></label>
						<input class="registry-input" type="email" name="email" required maxlength="256" autocomplete="email" />
					</div>
					<div class="registry-form-row">
						<button type="submit" class="btn registry-btn-primary" id="registry-resend-btn">Send My Link</button>
					</div>
				</form>
			</div>`;
		el('registry-resend-form').addEventListener('submit', onResendSubmit);
	}

	async function onResendSubmit(e) {
		e.preventDefault();
		const email  = e.target.email.value.trim();
		const msgEl  = el('registry-msg');
		const btn    = el('registry-resend-btn');
		if (!email) { msgEl.innerHTML = msgHtml('error', 'Please enter your email address.'); return; }
		btn.disabled = true; btn.textContent = 'Sending\u2026';
		await apiFetch('/registry/resend-link', { method: 'POST', body: { email } });
		// Always show success — do not reveal whether the email exists
		msgEl.innerHTML = msgHtml('success', "If a registry exists for that email, we\u2019ve sent the management link.");
		btn.disabled = false; btn.textContent = 'Send My Link';
	}

	// ============================================================
	// PRODUCT PAGE: "Add to Registry" button
	// ============================================================
	async function initProductPage() {
		const btn = el('registry-add-to-registry-btn');
		if (!btn) return;
		if (!hasSession()) {
			btn.style.display = 'none';
			return;
		}
		btn.style.removeProperty('display');

		// Show and wire up "My Registry" link
		const myRegBtn = el('registry-my-registry-btn');
		if (myRegBtn) {
			const s = getSession();
			myRegBtn.href = `${window.location.origin}/pages/registry-manage?r=${encodeURIComponent(s.shareToken)}&m=${encodeURIComponent(s.manageToken)}`;
			myRegBtn.style.removeProperty('display');
		}

		// Check if this variant is already on the registry
		const session        = getSession();
		const shopifyVariantId = parseInt(btn.dataset.variantId || '0');
		const res = await apiFetch(
			`/registry/${encodeURIComponent(session.shareToken)}/manage`,
			{ manageToken: session.manageToken }
		);
		if (res.ok) {
			const existing = res.data?.items?.find(i => i.shopifyVariantId == shopifyVariantId);
			if (existing) {
				setRemoveMode(btn, existing.registryItemId);
				return;
			}
		}
		setAddMode(btn);
	}

	function setAddMode(btn) {
		btn.textContent = 'Add to Registry';
		btn.classList.remove('registry-btn--danger', 'registry-btn--added');
		btn.onclick = onAddToRegistry;
	}

	function setRemoveMode(btn, registryItemId) {
		btn.dataset.registryItemId = registryItemId;
		btn.textContent = 'Remove From Your Registry';
		btn.classList.add('registry-btn--danger');
		btn.classList.remove('registry-btn--added');
		btn.onclick = onRemoveFromRegistry;
	}

	async function onAddToRegistry() {
		const btn     = el('registry-add-to-registry-btn');
		const session = getSession();
		if (!session.shareToken || !session.manageToken) {
			window.alert('Please visit your management link first to enable \u201cAdd to Registry\u201d.');
			return;
		}

		// Product/variant IDs and price come from data attributes set by the Liquid snippet
		const shopifyProductId = parseInt(btn.dataset.productId || '0');
		const shopifyVariantId = parseInt(btn.dataset.variantId || '0');
		const btnPrice         = parseFloat(btn.dataset.price || '0');
		if (!shopifyVariantId) {
			window.alert('Please select a product option first.');
			return;
		}

		// Product data from Liquid data attributes
		const productTitle    = btn.dataset.productTitle || '';
		const variantTitle    = (btn.dataset.variantTitle === 'Default Title' ? null : btn.dataset.variantTitle) || null;
		const productImageUrl = btn.dataset.imageUrl || null;
		const productUrl      = window.location.pathname;

		btn.disabled    = true;
		btn.textContent = 'Adding\u2026';

		const res = await apiFetch(
			`/registry/${encodeURIComponent(session.shareToken)}/items`,
			{
				method:      'POST',
				manageToken: session.manageToken,
				body: {
					shopifyVariantId,
					shopifyProductId,
					productTitle:      productTitle || 'Unknown Product',
					variantTitle,
					price:             btnPrice,
					productImageUrl,
					productUrl,
					qtyDesired:        1
				}
			}
		);

		btn.disabled = false;
		if (res.ok) {
			setRemoveMode(btn, res.data.id);
		} else if (res.data?.error === 'already_on_registry') {
			// Shouldn't normally happen — initProductPage checks first — but handle gracefully
			setRemoveMode(btn, btn.dataset.registryItemId || 0);
		} else if (res.data?.error === 'unauthorized') {
			clearSession();
			btn.style.display = 'none';
			window.alert('Your registry session has expired. Please visit your management link to re-enable \u201cAdd to Registry\u201d.');
		} else {
			btn.textContent = 'Add to Registry';
			window.alert('Could not add item. Please try again.');
		}
	}

	async function onRemoveFromRegistry() {
		const btn            = el('registry-add-to-registry-btn');
		const session        = getSession();
		const registryItemId = btn.dataset.registryItemId;
		if (!registryItemId) return;

		btn.disabled    = true;
		btn.textContent = 'Removing\u2026';

		const res = await apiFetch(
			`/registry/${encodeURIComponent(session.shareToken)}/items/${registryItemId}`,
			{ method: 'DELETE', manageToken: session.manageToken }
		);

		btn.disabled = false;
		if (res.ok) {
			setAddMode(btn);
		} else {
			btn.textContent = 'Remove From Your Registry';
			window.alert('Could not remove item. Please try again.');
		}
	}

	// ============================================================
	// INIT / ROUTING
	// ============================================================
	function init() {
		const app = el('registry-app');
		if (app) {
			const page = app.dataset.page;
			switch (page) {
				case 'create':  initCreatePage();  break;
				case 'verify':  initVerifyPage();  break;
				case 'search':  initSearchPage();  break;
				case 'view':    initViewPage();    break;
				case 'manage':  initManagePage();  break;
				case 'resend':  initResendPage();  break;
			}
		} else if (el('registry-add-to-registry-btn')) {
			initProductPage();
		}
	}

	return { init, getSession, saveSession, clearSession, hasSession };
})();

document.addEventListener('DOMContentLoaded', BB.init);
